import type { FilterOption } from "@/components/BlotterFilterBar";
import type {
  ReminderBasis,
  SendQueueItem,
  SendQueueKind,
  SendQueueWindow,
} from "@/interfaces/sendQueueCampaigns";
import type { CsvColumn } from "@/lib/csv";

/** Column ids for the seven Send Queue table columns (matches sendQueueCampaignsColumns.tsx's key order). */
export type SendQueueColumnId =
  | "campaign"
  | "organization"
  | "send_type"
  | "recipients"
  | "scheduled_at"
  | "status"
  | "actions";

/**
 * Column id -> header label, mirroring how constants/adminContacts.ts exposes its own
 * COLUMN_LABELS -- sendQueueCampaignsColumns.tsx imports this the same way adminContactsColumns.tsx
 * imports its sibling, instead of restating the seven labels as a local object.
 */
export const COLUMN_LABELS: Record<SendQueueColumnId, string> = {
  campaign: "Campaign",
  organization: "Organization",
  send_type: "Send Type",
  recipients: "Recipients",
  scheduled_at: "Scheduled",
  status: "Status",
  actions: "Actions",
};

/** Rows per page (ticket-specified). */
export const PAGE_SIZE = 100;

/** Table minimum width before horizontal scroll. */
export const MIN_WIDTH = 1100;

/**
 * All user-facing copy for the Send Queue tab -- other units import these strings from here; no
 * component may hardcode a UI string. Functions are used for every templated string (recipient
 * counts, campaign names, timestamps) so no component does string surgery of its own.
 */
export const COPY = {
  emptyState: "No campaigns match these filters.",
  /** Shown for a row whose scheduled moment has arrived but has not flipped to Sending yet. */
  dueNow: "Due now",
  /**
   * Failure-banner copy, in the style of AdminContactsPanel.tsx's FAILURE_COPY but split into
   * title + body because this banner renders an <AlertTitle>. Send Queue has the same three
   * failure surfaces that panel has -- the list, the fetched filter options (`accountLeads` is
   * the only reference data here, D8) and both at once -- so all three are spelled out. The
   * options-only wording never claims the table is broken: that request failing empties the
   * Account Lead menu and nothing else.
   */
  failure: {
    title: "Couldn't load the send queue",
    body: "Check your connection and try again.",
    optionsTitle: "Couldn't load the filter options",
    optionsBody: "The Account Lead menu is empty. The table below is unaffected.",
    bothTitle: "Couldn't load the send queue or the filter options",
    bothBody: "Check your connection and try again.",
    retry: "Retry",
  },
  /**
   * Footer/pager and empty-state noun ("100 campaigns · Page 1 of 3"). Deliberately NOT the
   * Blotter's default "row", which would read "100 rows" under an empty state that says "No
   * campaigns match these filters."; the Blotter derives the plural itself.
   */
  itemNoun: "campaign",
  /** Labels of the Blotter's four filter menus, in display order. */
  filterLabels: {
    campaignType: "Campaign Type",
    sendType: "Send Type",
    status: "Status",
    accountLead: "Account Lead",
  },
  windowLabel: "Time Window",
  searchPlaceholder: "Search and filter campaigns",
  reschedule: {
    label: "New send time",
    note: "Recipients are notified at the new time",
    errors: {
      pastTime: "New send time must be in the future",
      afterExpiry: "A reminder must be scheduled before the gift expires",
    },
  },
  sendNowTitle: (n: number) => `Send to ${n} recipients now?`,
  cancel: {
    title: "Cancel this send?",
    body: (n: number) => `${n} recipients will not receive this email.`,
  },
  toast: {
    paused: (campaign: string) => `Paused: ${campaign}`,
    resumed: (campaign: string) => `Resumed: ${campaign}`,
    sendingNow: (n: number) => `Sending ${n} gifts now…`,
    rescheduled: (when: string) => `Rescheduled to ${when}`,
    cancelled: (sendLabel: string) => `Send cancelled: ${sendLabel}`,
    /**
     * Answer to a Confirm that arrives while that same row's irreversible mutation is still in
     * flight (a reopened dialog, a stale render). The second confirm is refused, not queued.
     */
    actionInFlight: "That send is already being processed — wait for it to finish.",
    /**
     * Answer to any row action (menu item or modal) fired while that same row still has a
     * mutation in flight OR is waiting for the refetch that follows it — until that refetch
     * lands the table is still showing the row's pre-action state, so a second action would be
     * taken against data the user can see but the server no longer has.
     */
    rowBusy: "This send is still updating — wait for the table to refresh before trying again.",
    /**
     * Fallback wording when the CSV export request fails without the API supplying a sentence of
     * its own (`apiErrorMessage` prefers the server's message when there is one).
     */
    exportFailed: "Couldn't export the send queue. Check your connection and try again.",
  },
  /** Row menu item labels, in display order. */
  rowMenu: {
    pause: "Pause",
    resume: "Resume",
    sendNow: "Send now",
    reschedule: "Reschedule…",
    viewEmail: "View email",
    viewCampaign: "View campaign",
    viewContacts: "View contacts",
    cancelSend: "Cancel send",
    /** Screen-reader label for the per-row three-dot menu button (SendQueueRowMenu.tsx). */
    actionsLabel: (campaign: string) => `Actions for ${campaign}`,
  },
  /** IconButton aria-label shared by every dialog's close control (ViewContactsDialog, ViewEmailDialog). */
  closeLabel: "Close",
  /**
   * Generic Cancel/Save button labels shared by the Send Queue's confirm/form modals --
   * ConfirmActionModal passes `cancel` as GfgModal's `secondaryLabel`; RescheduleModal
   * uses both for its own GfgButton pair.
   */
  modal: {
    cancel: "Cancel",
    save: "Save",
  },
  /** ViewContactsDialog.tsx's table headers, dialog title and "shown of total" truncation line. */
  contactsDialog: {
    title: "View contacts",
    columns: {
      recipient: "Recipient",
      email: "Email",
      status: "Status",
    },
    showingOf: (shown: number, total: number) => `Showing ${shown} of ${total} contacts`,
  },
  /** ViewEmailDialog.tsx's dialog title. */
  emailDialog: {
    title: "View email",
  },
} as const;

/** Time Window select options + default (D8: fixed frontend vocabulary, not fetched). */
export const WINDOW_OPTIONS: FilterOption[] = [
  { value: "1", label: "Next 24 hours" },
  { value: "7", label: "Next 7 days" },
  { value: "30", label: "Next 30 days" },
  { value: "all", label: "All upcoming" },
];
export const DEFAULT_WINDOW: SendQueueWindow = "7";

/** Row-cell labels for each send kind (e.g. the table's "Send" column). */
export const SEND_TYPE_LABELS: Record<SendQueueKind, string> = {
  gift: "Gift email",
  reminder: "Reminder",
  birthday: "Birthday gifts",
  anniversary: "Anniversary gifts",
};

/**
 * The Send Type filter's options -- the ticket's own vocabulary, deliberately different from
 * SEND_TYPE_LABELS above (singular "Birthday"/"Anniversary", no "gifts"). Do not unify the two.
 */
export const SEND_TYPE_FILTER_OPTIONS: FilterOption[] = [
  { value: "gift", label: "Gift email" },
  { value: "reminder", label: "Reminder" },
  { value: "birthday", label: "Birthday" },
  { value: "anniversary", label: "Anniversary" },
];

/**
 * Sub-labels shown under the primary send label. `reminderBasis` keys off `ReminderBasis` (for
 * "reminder" rows); `kind` keys off the other three kinds' fixed descriptions (there is no
 * "reminder" entry in `kind` -- a reminder row's sub-label always comes from `reminderBasis`).
 */
export const SEND_SUB_LABELS: {
  reminderBasis: Record<ReminderBasis, string>;
  kind: Record<Exclude<SendQueueKind, "reminder">, string>;
} = {
  reminderBasis: {
    after_send: "after gift email",
    before_expiry: "before gift expiry",
  },
  kind: {
    gift: "Single send to all recipients",
    birthday: "Triggered on recipient date",
    anniversary: "Triggered on recipient date",
  },
};

/** Campaign Type filter options (D8: fixed frontend vocabulary). */
export const CAMPAIGN_TYPE_OPTIONS: FilterOption[] = [
  { value: "Bulk Send", label: "Bulk Send" },
  { value: "Automated", label: "Automated" },
];

/** CSV export filename. */
export const CSV_FILENAME = "send-queue.csv";

/** CSV export columns, in header order. */
export const CSV_COLUMNS: CsvColumn<SendQueueItem>[] = [
  { label: "scheduled_at", value: (row) => row.scheduledAt },
  { label: "campaign_type", value: (row) => row.campaignType },
  { label: "organization", value: (row) => row.organization },
  { label: "campaign", value: (row) => row.campaignTitle },
  { label: "send", value: (row) => row.sendLabel },
  { label: "recipients", value: (row) => String(row.recipients) },
  { label: "status", value: (row) => row.status },
];
